import { Trans } from '@lingui/react/macro';
import type { HTMLAttributes } from 'react';

import { Text } from '@/components/typography';
import { numberFormat } from '@/helper/numberFormatter';
import { cn } from '@/lib/utils';

type Props = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
  percentChange: number | null;
  colorize?: boolean;
};

const getDeltaType = (percentChange: number | null) => {
  if (percentChange === null) {
    return 'neutral';
  }

  if (Math.abs(percentChange) < 0.005) {
    return 'neutral';
  }

  if (percentChange > 0) {
    return 'positive';
  } else if (percentChange < 0) {
    return 'negative';
  } else {
    return 'neutral';
  }
};

const DeltaLabel = ({ percentChange, colorize = false, className, ...rest }: Props) => {
  const deltaType = getDeltaType(percentChange);
  const absValue = percentChange === null ? null : Math.abs(percentChange);

  return (
    <div className={cn('flex items-center justify-start gap-0.5', className)} {...rest}>
      <div
        className={cn(
          'size-0 border-x-8 border-b-14 border-solid border-x-transparent',
          deltaType === 'positive' && 'rotate-0 border-b-green-400',
          deltaType === 'negative' && 'rotate-180 border-b-red-400',
          deltaType === 'neutral' && 'rotate-90 border-b-gray-500',
        )}
      />
      <Text
        variant="caption1"
        color={colorize && deltaType === 'negative' ? 'red' : 'black'}
        className={colorize && deltaType === 'positive' ? 'text-teal-600' : undefined}
      >
        {deltaType === 'positive' && '+'}
        {deltaType === 'negative' && '-'}
        {percentChange === null ? (
          <Text variant="caption1" color="grey">
            <Trans>N/A</Trans>
          </Text>
        ) : (
          numberFormat(absValue, { isPercent: true, precision: 0 })
        )}
      </Text>
    </div>
  );
};

export default DeltaLabel;
